// Debt detail — full parity. Per-variant summaries + SVG charts (Phase 4A) plus
// the write flows (4B): payment reconciliation, statement upload/download,
// mortgage valuations, facility reconcile, card-activity month view, coherence
// banner, class/payment-source edit, delete. All allocation/activity/coherence
// logic reused unchanged from the shared lib.
import { h, mount, type Child } from '../core/dom';
import { signal, computed, effect, type Signal } from '../core/signal';
import { bindInput, text } from '../core/bind';
import { navigate } from '../core/router';
import { changeMonth, getCurrentMonth } from '@lib/month';
import { paidDownPercent, cardCoherence, cardActivity, validateAllocations } from '@lib/debt';
import { buildAllocations, chargeFieldKey } from '@lib/debtReconcile';
import {
	buildFacilityChartOptions,
	buildInterestOptions,
	buildTrajectoryOptions,
	computeProjections,
	customPaymentDefault,
	payoffText,
} from '@lib/debtDetailCharts';
import {
	DEBT_TYPES,
	formatError,
	type DebtView,
	type PaymentSuggestion,
	type PaymentView,
	type Allocation,
} from '@lib/db';
import { formatCurrency } from '@lib/format';
import { renderChart, type ChartSpec } from '../components/svgChart';
import { monthNav } from '../components/monthNav';
import { setHeaderControl } from '../components/headerSlot';
import {
	loadDebt,
	loadMonthTransactions,
	loadPaymentSuggestion,
	updateDebt,
	deleteDebt,
	reconcilePayment,
	unreconcilePayment,
	reconcileBalance,
	deleteReconcile,
	saveValuation,
	deleteValuation,
	uploadStatement,
	getStatementDownloadUrl,
} from '../data';
import { confirmDialog } from '../ui/modal';
import { toast } from '../ui/toast';
import { loading } from '../ui/loading';

export function debtDetailRoute(params: Record<string, string>): Node {
	const id = params.id ?? '';
	const container = h('div', {});
	setHeaderControl(null);
	const debt = signal<DebtView | null>(null);
	const error = signal<string | null>(null);

	function reload(): void {
		debt.set(null);
		error.set(null);
		loadDebt(id)
			.then((d) => debt.set(d))
			.catch((e) => error.set(formatError(e as Error)));
	}

	mount(container, () => {
		if (error()) return h('div', { class: 'state-error' }, error());
		const d = debt();
		if (!d) return loading();
		return [
			h('div', { class: 'row' }, h('a', { class: 'btn btn-sm btn-secondary', href: '#/debts' }, '← Debts'), deleteButton(d)),
			h('h1', {}, d.name),
			editBar(d),
			variantView(d, reload),
		];
	});

	reload();
	return container;

	function deleteButton(d: DebtView): Child {
		return h('button', { class: 'btn btn-sm btn-danger spacer', onclick: () => {
			confirmDialog(`Delete debt "${d.name}"? Reconciled payments keep their splits.`, 'Delete').then((ok) => {
				if (!ok) return;
				deleteDebt(id).then(() => navigate('/debts')).catch((e) => toast(formatError(e as Error)));
			});
		} }, 'Delete');
	}

	function editBar(d: DebtView): Child {
		const cls = selectControl(d.debtClass ?? 'consumer', [['consumer', 'Consumer'], ['asset_backed', 'Asset-backed'], ['monitored', 'Monitored']]);
		cls.addEventListener('change', () => updateDebt(id, { debtClass: cls.value as DebtView['debtClass'] }).catch((e) => toast(formatError(e as Error))));
		const src = selectControl(d.paymentSource ?? 'bank', [['bank', 'Bank'], ['payroll', 'PAYE']]);
		src.addEventListener('change', () => updateDebt(id, { paymentSource: src.value as DebtView['paymentSource'] }).catch((e) => toast(formatError(e as Error))));
		return h('div', { class: 'row' }, h('span', { class: 'badge' }, d.debtType.replace(/_/g, ' ')), labeledInline('Class', cls), labeledInline('Paid from', src));
	}
}

function variantView(d: DebtView, reload: () => void): Child {
	if (d.debtType === DEBT_TYPES.INTEREST_FREE_FACILITY) return facilityView(d, reload);
	if (d.debtType === DEBT_TYPES.MORTGAGE) return mortgageView(d, reload);
	return cardView(d, reload);
}

// ---------------------------------------------------------------- facility ----
function facilityView(d: DebtView, reload: () => void): Child {
	const pct = paidDownPercent(d.originalPrincipal ?? 0, d.currentBalance ?? 0);
	const guard =
		d.guardStatus === 'overdue' || d.guardStatus === 'due'
			? h('div', { class: 'state-error' }, `Minimum payment ${d.guardStatus}${d.guardDueDate ? ` (${d.guardDueDate})` : ''}`)
			: null;

	const customInput = signal('');
	const effectiveCustom = computed(() => {
		const n = Number(customInput());
		return n > 0 ? n : customPaymentDefault(d);
	});
	const projections = computed(() => computeProjections(d, effectiveCustom(), new Date()));
	const chartBox = h('div', {});
	mount(chartBox, () => chartOrNull(buildFacilityChartOptions(d, projections(), effectiveCustom()) as ChartSpec | null));
	const predInput = h('input', { class: 'input', inputmode: 'decimal', placeholder: 'Predicted payment' }) as HTMLInputElement;
	bindInput(predInput, customInput);

	return h(
		'div',
		{},
		guard,
		summary(
			line('Current balance', formatCurrency(d.currentBalance ?? 0)),
			progress(pct),
			line('Paid down', `${pct}%`),
			line('Expected balance', formatCurrency(d.expectedBalance ?? 0)),
			line('Ahead/behind', formatCurrency(d.aheadBehind ?? 0), (d.aheadBehind ?? 0) >= 0 ? 'positive' : 'negative'),
			line('Minimum payment', formatCurrency(d.minimumPayment ?? 0))
		),
		chartBox,
		h(
			'div',
			{ class: 'card' },
			h('div', { class: 'chart-title' }, 'Payoff prediction'),
			labeledInline('Predicted payment', predInput),
			h('div', { class: 'muted' }, text(() => `At minimum: ${payoffText(projections()?.min ?? null)}`)),
			h('div', { class: 'muted' }, text(() => `At ${formatCurrency(effectiveCustom())}: ${payoffText(projections()?.custom ?? null)}`))
		),
		definitionCard(d, reload),
		balanceHistory(d, reload),
		paymentHistory(d, reload)
	);
}

// The facility definition drives the payment guard (the period anchors on the
// start date's DAY; the minimum is due by the penalty day each period) and the
// expected trajectory. Editable here because a mis-anchored definition
// otherwise surfaces only as phantom due/overdue alerts with no way to see why.
function definitionCard(d: DebtView, reload: () => void): Child {
	const patch = (updates: Parameters<typeof updateDebt>[1]) =>
		updateDebt(d.id, updates).then(reload).catch((e) => toast(formatError(e as Error)));

	const start = h('input', { class: 'input', type: 'date', value: d.startDate ?? '', 'aria-label': 'Start date' }) as HTMLInputElement;
	start.addEventListener('change', () => void patch({ startDate: start.value }));
	const end = h('input', { class: 'input', type: 'date', value: d.endDate ?? '', 'aria-label': 'End date (term)' }) as HTMLInputElement;
	end.addEventListener('change', () => void patch({ endDate: end.value }));
	const freq = selectControl(d.paymentFrequency ?? 'monthly', [['monthly', 'Monthly'], ['four-weekly', 'Four-weekly'], ['fortnightly', 'Fortnightly'], ['weekly', 'Weekly']]);
	freq.addEventListener('change', () => void patch({ paymentFrequency: freq.value as DebtView['paymentFrequency'] }));
	const penalty = h('input', { class: 'input', type: 'number', min: '1', max: '31', value: d.penaltyDeadline ?? '', placeholder: 'e.g. 9', 'aria-label': 'Penalty day of month' }) as HTMLInputElement;
	penalty.addEventListener('change', () => void patch({ penaltyDeadline: penalty.value.trim() }));
	const min = h('input', { class: 'input', type: 'number', step: '0.01', value: String(d.minimumPayment ?? 0), 'aria-label': 'Minimum payment' }) as HTMLInputElement;
	min.addEventListener('change', () => void patch({ minimumPayment: Number(min.value) || 0 }));

	return h(
		'div',
		{ class: 'card' },
		h('div', { class: 'chart-title' }, 'Definition'),
		h(
			'p',
			{ class: 'muted definition-note' },
			`Payment periods anchor on the start date's day of month; the minimum is due by the penalty day of each period (rolling into the next month when the period starts after it). Payments are matched via the "${d.paymentCategory || '—'}" category.`
		),
		h(
			'div',
			{ class: 'form-grid' },
			labeledInline('Start date', start),
			labeledInline('End date (term)', end),
			labeledInline('Frequency', freq),
			labeledInline('Penalty day', penalty),
			labeledInline('Minimum payment', min)
		)
	);
}

// Facility balance reconcile (date + balance + optional statement PDF) + history.
function balanceHistory(d: DebtView, reload: () => void): Child {
	const date = h('input', { class: 'input', type: 'date', 'aria-label': 'Statement date' }) as HTMLInputElement;
	const balance = h('input', { class: 'input', type: 'number', step: '0.01', placeholder: 'Balance from statement', 'aria-label': 'Balance from statement' }) as HTMLInputElement;
	const file = h('input', { class: 'input', type: 'file', accept: 'application/pdf', 'aria-label': 'Statement PDF' }) as HTMLInputElement;
	const busy = signal(false);
	const submit = async () => {
		const bal = Number(balance.value);
		if (!Number.isFinite(bal) || balance.value.trim() === '') return;
		busy.set(true);
		try {
			let statementKey: string | undefined;
			let statementName: string | undefined;
			if (file.files && file.files[0]) {
				const up = await uploadStatement(d.id, file.files[0]);
				statementKey = up.key;
				statementName = up.name;
			}
			await reconcileBalance(d.id, bal, { date: date.value || undefined, statementKey, statementName });
			reload();
		} catch (e) {
			toast(formatError(e as Error));
		} finally {
			busy.set(false);
		}
	};
	// In-flight guard: the save button follows busy (uploads can take a while;
	// a second click mid-upload would double-reconcile).
	const saveBtn = h('button', { class: 'btn btn-sm btn-primary', onclick: () => void submit() }, 'Save') as HTMLButtonElement;
	effect(() => (saveBtn.disabled = busy()));
	const rows = d.reconciles ?? [];
	return h(
		'div',
		{ class: 'card' },
		h('div', { class: 'chart-title' }, 'Balance history'),
		h('div', { class: 'row' }, date, balance, file, saveBtn),
		rows.length === 0
			? h('p', { class: 'muted' }, 'No reconciles yet.')
			: h(
					'table',
					{ class: 'history' },
					h('thead', {}, h('tr', {}, h('th', {}, 'Date'), h('th', { class: 'num' }, 'Balance'), h('th', {}, 'Statement'), h('th', {}, ''))),
					h('tbody', {}, ...rows.map((r) =>
						h(
							'tr',
							{},
							h('td', {}, r.date),
							h('td', { class: 'num' }, formatCurrency(r.balance)),
							h('td', {}, r.statementKey ? h('button', { class: 'btn-link', onclick: () => openStatement(d.id, r.statementKey!) }, `${r.statementName || 'Download'} ↓`) : '—'),
							h('td', {}, h('button', { class: 'x-btn', 'aria-label': 'Delete', onclick: () => deleteReconcile(d.id, r.date).then(reload).catch((e) => toast(formatError(e as Error))) }, '×'))
						)
					))
				)
	);
}

// ---------------------------------------------------------------- mortgage ----
function mortgageView(d: DebtView, reload: () => void): Child {
	const value = h('input', { class: 'input', type: 'number', min: '0', placeholder: 'Estimated value (NZD)', 'aria-label': 'Estimated value (NZD)' }) as HTMLInputElement;
	const date = h('input', { class: 'input', type: 'date', 'aria-label': 'Valuation date' }) as HTMLInputElement;
	const note = h('input', { class: 'input', placeholder: 'Note (optional)', 'aria-label': 'Valuation note' }) as HTMLInputElement;
	const submit = () => {
		const v = Number(value.value);
		if (!Number.isFinite(v) || value.value.trim() === '') return;
		valSaveBtn.disabled = true; // in-flight guard
		saveValuation(d.id, v, { date: date.value || undefined, note: note.value || undefined })
			.then(reload)
			.catch((e) => {
				valSaveBtn.disabled = false;
				toast(formatError(e as Error));
			});
	};
	const valSaveBtn = h('button', { class: 'btn btn-sm btn-primary', onclick: submit }, 'Save') as HTMLButtonElement;
	const history = d.valuationHistory ?? [];
	return h(
		'div',
		{},
		summary(
			line('Total balance', formatCurrency(d.totalBalance ?? 0)),
			h('div', { class: 'summary-sub muted' }, `across ${(d.tranches ?? []).length} tranche${(d.tranches ?? []).length === 1 ? '' : 's'} · live from Akahu`),
			line('Equity', d.equity != null ? `${formatCurrency(d.equity)} (${d.equityPct ?? 0}%)` : '—'),
			line('LVR', `${d.lvrPct ?? 0}%`),
			line('Valuation as of', d.valuationAsOf ?? '—')
		),
		(d.tranches ?? []).length === 0
			? h('p', { class: 'muted' }, 'No loan accounts linked yet.')
			: h(
					'table',
					{ class: 'history' },
					h('thead', {}, h('tr', {}, h('th', {}, 'Tranche'), h('th', { class: 'num' }, 'Balance'), h('th', {}, 'Updated'))),
					h('tbody', {}, ...(d.tranches ?? []).map((t) => h('tr', {}, h('td', {}, t.name), h('td', { class: 'num' }, formatCurrency(t.balance)), h('td', {}, t.updatedAt ?? ''))))
				),
		h(
			'div',
			{ class: 'card' },
			h('div', { class: 'chart-title' }, 'Property valuation'),
			h('div', { class: 'row' }, value, date, note, valSaveBtn),
			history.length === 0
				? h('p', { class: 'muted' }, 'No valuations yet.')
				: h(
						'table',
						{ class: 'history' },
						h('thead', {}, h('tr', {}, h('th', {}, 'Date'), h('th', { class: 'num' }, 'Amount'), h('th', {}, 'Note'), h('th', {}, ''))),
						h('tbody', {}, ...history.map((v) =>
							h('tr', {}, h('td', {}, v.date), h('td', { class: 'num' }, formatCurrency(v.amount)), h('td', {}, v.note ?? ''), h('td', {}, h('button', { class: 'x-btn', 'aria-label': 'Delete', onclick: () => deleteValuation(d.id, v.date).then(reload).catch((e) => toast(formatError(e as Error))) }, '×')))
						))
					)
		)
	);
}

// ------------------------------------------------------------- credit card ----
function cardView(d: DebtView, reload: () => void): Child {
	const pct = paidDownPercent(d.legacyBalanceStart ?? 0, d.legacyRemaining);
	const coh = cardCoherence(d);
	return h(
		'div',
		{},
		coh ? h('div', { class: 'coherence-banner' }, `Live balance is ${formatCurrency(coh.actual)}; ledger expects ${formatCurrency(coh.expected)} (~${formatCurrency(Math.abs(coh.gap))} likely in unreconciled payments).`) : null,
		summary(
			d.linkedAccountBalance != null && d.linkedAccountBalance !== 0 ? line('Card balance', formatCurrency(Math.abs(d.linkedAccountBalance))) : null,
			line('Legacy remaining', formatCurrency(d.legacyRemaining), d.isLegacyActive ? '' : 'positive'),
			progress(pct),
			line('Paid down', `${pct}%`),
			line('Minimum payment', formatCurrency(d.minimumPayment ?? 0)),
			line('Target balance', formatCurrency(d.targetBalance ?? 0))
		),
		activitySection(d),
		chartOrNull(buildInterestOptions(d) as ChartSpec | null),
		chartOrNull(buildTrajectoryOptions(d) as ChartSpec | null),
		reconcileSection(d, reload),
		paymentHistory(d, reload)
	);
}

// Card-activity month view (independent month nav, recomputed via cardActivity).
function activitySection(d: DebtView): Child {
	const month = signal(getCurrentMonth());
	const activity = signal<ReturnType<typeof cardActivity> | null>(null);
	// Load as an effect so the month() read is tracked here, not by the page-level
	// mount — a subscription there would re-render the whole page on month.set(),
	// recreating this section (and its signal) back at the current month. Guard
	// against a stale fetch landing after another navigation.
	effect(() => {
		const m = month();
		activity.set(null);
		loadMonthTransactions(m)
			.then((txs) => { if (month() === m) activity.set(cardActivity(txs, d)); })
			.catch(() => { if (month() === m) activity.set(cardActivity([], d)); });
	});
	const nav = h('div', {});
	mount(nav, () => monthNav(month(), (off) => month.set(changeMonth(month(), off))));
	const box = h('div', {});
	mount(box, () => {
		const a = activity();
		if (!a) return loading('Loading activity…');
		if (a.count === 0 && a.creditsIn === 0) return h('p', { class: 'muted' }, 'No card activity this cycle.');
		return [
			h(
				'div',
				{ class: 'stats-grid' },
				stat('Spend', formatCurrency(a.totalSpend)),
				stat('Interest & fees', formatCurrency(a.interestTotal)),
				stat('Credits in', formatCurrency(a.creditsIn)),
				stat('Charges', String(a.count))
			),
			h('ul', { class: 'split-lines' }, ...a.byCategory.map((c) => h('li', {}, h('span', {}, `${c.category} (${c.count})`), h('span', {}, formatCurrency(c.total))))),
			h('p', { class: 'muted activity-note' }, "This spend is already counted on the dashboard/budget at point of purchase — this is the card's own activity for the cycle."),
		];
	});
	return h(
		'div',
		{ class: 'card' },
		h('div', { class: 'chart-title' }, 'Card activity'),
		nav,
		box
	);
}

// Reconcile unreconciled payments (allocation editor) + payment history.
function reconcileSection(d: DebtView, reload: () => void): Child {
	const openSk = signal<string | null>(null);
	const editor = signal<{ sk: string; node: Child } | null>(null);
	const unrec = d.unreconciledPayments ?? [];
	const wrap = h('div', { class: 'card' });
	wrap.append(h('div', { class: 'chart-title' }, 'Unreconciled payments'));
	if (unrec.length === 0) {
		wrap.append(h('p', { class: 'muted' }, 'No unreconciled payments.'));
		return wrap;
	}
	const list = h('div', {});
	mount(list, () =>
		unrec.map((p) => {
			const region = h('div', {});
			mount(region, () => {
				const ed = editor();
				return openSk() === p.sk && ed?.sk === p.sk ? ed.node : null;
			});
			return h(
				'div',
				{ class: 'pay-row' },
				h('span', {}, `${p.date?.slice(0, 10)} · ${p.description || 'Payment'}`),
				h('span', { class: 'spacer' }, formatCurrency(Math.abs(p.amount))),
				h('button', { class: 'btn btn-sm btn-secondary', onclick: () => openReconcile(d, p, openSk, editor, reload) }, 'Reconcile'),
				region
			);
		})
	);
	wrap.append(list);
	return wrap;
}

function openReconcile(
	d: DebtView,
	p: PaymentView,
	openSk: Signal<string | null>,
	editor: Signal<{ sk: string; node: Child } | null>,
	reload: () => void
): void {
	if (openSk() === p.sk) {
		openSk.set(null);
		editor.set(null);
		return;
	}
	loadPaymentSuggestion(d.id, p.pk, p.sk)
		.then((sg) => {
			editor.set({ sk: p.sk, node: reconcileEditor(d, p, sg, () => { openSk.set(null); editor.set(null); }, reload) });
			openSk.set(p.sk);
		})
		.catch((e) => toast(formatError(e as Error)));
}

function reconcileEditor(d: DebtView, p: PaymentView, sg: PaymentSuggestion, close: () => void, reload: () => void): Child {
	const chargeAmt = new Map<string, Signal<string>>();
	for (const c of sg.unpaidPool) {
		const seed = sg.allocations.find((a) => a.kind === 'purchase' && a.targetPk === c.pk && a.targetSk === c.sk);
		chargeAmt.set(chargeFieldKey(c.pk, c.sk), signal(seed ? String(seed.amount) : '0'));
	}
	const legacyAmt = signal(String(sg.allocations.find((a) => a.kind === 'legacy')?.amount ?? '0'));
	const role = signal(sg.role);

	const recordObj = (): Record<string, string> => {
		const r: Record<string, string> = {};
		for (const [k, s] of chargeAmt) r[k] = s();
		return r;
	};
	const allocations = computed<Allocation[]>(() => buildAllocations(sg, recordObj(), legacyAmt()));
	// Validate against the suggestion's positive magnitude — p.amount is negative
	// (spend convention), which can never equal a sum of positive allocations.
	const errorMsg = computed(() => validateAllocations(sg.magnitude, allocations(), sg.unpaidPool, sg.legacyRemaining, d.legacyCategory ?? ''));
	const overpayment = computed(() => allocations().filter((a) => a.kind === 'overpayment').reduce((s, a) => s + a.amount, 0));

	const chargeRows = sg.unpaidPool.map((c) => {
		const sigc = chargeAmt.get(chargeFieldKey(c.pk, c.sk))!;
		const input = h('input', { class: 'input', inputmode: 'decimal', 'aria-label': `Allocation for ${c.description}` }) as HTMLInputElement;
		bindInput(input, sigc);
		return h('div', { class: 'row' }, h('span', {}, `${c.date} · ${c.description} (${c.category})`), h('span', { class: 'spacer muted' }, `unpaid ${formatCurrency(c.unpaid)}`), input);
	});
	const legacyInput = h('input', { class: 'input', inputmode: 'decimal', 'aria-label': 'Legacy paydown amount' }) as HTMLInputElement;
	bindInput(legacyInput, legacyAmt);
	const roleSel = selectControl(role(), [['minimum', 'minimum'], ['extra', 'extra'], ['mixed', 'mixed']]);
	bindInput(roleSel, role as Signal<string>, 'change');

	const errEl = h('span', { class: 'state-error' }, text(() => errorMsg() ?? ''));
	effect(() => errEl.classList.toggle('hidden', !errorMsg()));
	const saveBtn = h('button', { class: 'btn btn-sm btn-primary', onclick: () => {
		if (errorMsg() || saveBtn.disabled) return;
		saveBtn.disabled = true; // in-flight guard
		reconcilePayment(d.id, p.pk, p.sk, allocations(), role())
			.then(reload)
			.catch((e) => {
				saveBtn.disabled = false;
				toast(formatError(e as Error));
			});
	} }, 'Save') as HTMLButtonElement;
	effect(() => (saveBtn.disabled = errorMsg() !== null));

	return h(
		'div',
		{ class: 'reconcile-editor' },
		h('div', { class: 'muted' }, `Allocate ${formatCurrency(sg.magnitude)} across the charges it clears (a transfer) and any legacy paydown (a cost). Defaults clear the oldest charges first.`),
		...chargeRows,
		h('div', { class: 'row legacy-alloc' }, h('span', {}, 'Legacy paydown', h('span', { class: 'cost-tag' }, 'cost')), h('span', { class: 'spacer muted' }, `remaining ${formatCurrency(sg.legacyRemaining)}`), legacyInput),
		h('div', { class: 'row' }, h('span', { class: 'muted' }, text(() => `Overpayment: ${formatCurrency(overpayment())}`))),
		labeledInline('Role', roleSel),
		errEl,
		h('div', { class: 'row' }, saveBtn, h('button', { class: 'btn btn-sm btn-secondary', onclick: close }, 'Cancel'))
	);
}

function paymentHistory(d: DebtView, reload: () => void): Child {
	const payments = d.payments ?? [];
	if (payments.length === 0) return h('div', { class: 'card' }, h('div', { class: 'chart-title' }, 'Payment history'), h('p', { class: 'muted' }, 'No reconciled payments yet.'));
	return h(
		'div',
		{ class: 'card' },
		h('div', { class: 'chart-title' }, 'Payment history'),
		h(
			'table',
			{ class: 'history' },
			h(
				'thead',
				{},
				h(
					'tr',
					{},
					h('th', {}, 'Date'),
					h('th', { class: 'num' }, 'Amount'),
					h('th', { class: 'num' }, 'Negating'),
					h('th', { class: 'num' }, 'Carried'),
					h('th', { class: 'num' }, 'Legacy (cost)'),
					h('th', { class: 'num' }, 'Over'),
					h('th', {}, 'Role'),
					h('th', {}, '')
				)
			),
			h('tbody', {}, ...payments.map((p) =>
				h(
					'tr',
					{},
					h('td', {}, p.date?.slice(0, 10)),
					h('td', { class: 'num' }, formatCurrency(Math.abs(p.amount))),
					h('td', { class: 'num' }, formatCurrency(p.negatingCurrent)),
					h('td', { class: 'num' }, formatCurrency(p.carriedPaydown)),
					h('td', { class: 'num cost' }, formatCurrency(p.legacyPaydown)),
					h('td', { class: 'num' }, formatCurrency(p.overpayment)),
					h('td', {}, p.role ?? ''),
					h('td', {}, h('button', { class: 'btn-link', onclick: () => unreconcilePayment(d.id, p.pk, p.sk).then(reload).catch((e) => toast(formatError(e as Error))) }, 'Undo'))
				)
			))
		)
	);
}

// ----- small shared builders -----
function summary(...rows: Child[]): Child {
	return h('div', { class: 'card' }, ...rows);
}
function line(label: string, value: string, cls = ''): Child {
	return h('div', { class: 'summary-line' }, h('span', { class: 'muted' }, label), h('span', { class: cls }, value));
}
function progress(pct: number): Child {
	return h(
		'div',
		{ class: 'progress-bar', role: 'progressbar', 'aria-label': 'Paid down', 'aria-valuemin': '0', 'aria-valuemax': '100', 'aria-valuenow': String(Math.round(pct)) },
		h('div', { class: 'progress-fill', style: { width: `${pct}%` } })
	);
}
function chartOrNull(spec: ChartSpec | null): Child {
	return spec ? renderChart(spec) : null;
}
function stat(label: string, value: string): Child {
	return h('div', { class: 'stat-card' }, h('div', { class: 'stat-label' }, label), h('div', { class: 'stat-value' }, value));
}
function labeledInline(label: string, control: HTMLElement): Child {
	return h('label', { class: 'row' }, h('span', { class: 'field-label' }, label), control);
}
function selectControl(value: string, opts: [string, string][]): HTMLSelectElement {
	const sel = h('select', { class: 'select' }) as HTMLSelectElement;
	for (const [v, l] of opts) sel.append(h('option', { value: v }, l));
	sel.value = value;
	return sel;
}
function openStatement(id: string, key: string): void {
	getStatementDownloadUrl(id, key)
		.then((url) => window.open(url, '_blank', 'noopener'))
		.catch((e) => toast(formatError(e as Error)));
}
